import { DriverAuthenticationManager } from '@frontend/authentication-v2';
import { emailRegex } from '@frontend/components/basic-forms';
import { Logger } from '@frontend/logger';
import { OrderClient } from '@frontend/order/api';
import type { Order } from '@frontend/order/types';
import { OrderStatusName } from '@frontend/order/types';
import type { IProduct } from '@frontend/product/types';
import { ProductType } from '@frontend/product/types';
import { SettingKey } from '@frontend/setting/types';
import type { IUser } from '@frontend/user/types';
import type { Dispatch } from 'react';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useScreen } from '../useScreen';

interface ViewProps {
    email: string;
    setEmail: Dispatch<string>;
    phoneNumber: string;
    setPhoneNumber: Dispatch<string>;
    submitDisabled: boolean;
    error: string | null;
    isSubmitting: boolean;
    submitted: boolean;
    continueEnabled: boolean;
    selectedServiceProduct?: IProduct;
    onSubmit: () => Promise<void>;
    onContinue: () => void;
    resetError: () => void;
}

type SubmissionState = 'idle' | 'submitting' | 'submitted' | 'error';

const useVerifyContactInformation = (): ViewProps => {
    const intl = useIntl();
    const { store, navigationState, wizardData, updateWizardData, onContinue, onHome, continueEnabled } = useScreen();
    const userOverwrite = wizardData.userOverwrite as IUser | undefined;
    const selectedUser = userOverwrite ?? navigationState.user?.user;
    const contact = wizardData.serviceProductContact;
    const [email, setEmailState] = useState<string>(contact?.email ?? selectedUser?.email ?? '');
    const [phoneNumber, setPhoneNumberState] = useState<string>(contact?.phoneNumber ?? selectedUser?.mobile ?? '');
    const [submissionState, setSubmissionState] = useState<SubmissionState>(
        wizardData.serviceProductContactSubmitted && wizardData.serviceProductOrderId ? 'submitted' : 'idle'
    );
    const isSubmittingRef = useRef(false);
    const [error, setError] = useState<string | null>(null);

    const trimmedEmail = email.trim();
    const trimmedPhoneNumber = phoneNumber.trim();
    const hasContactInformation = trimmedEmail.length > 0 || trimmedPhoneNumber.length > 0;
    const emailValid = trimmedEmail.length === 0 || emailRegex.test(trimmedEmail);
    const selectedProduct = wizardData.selectedServiceProduct ?? wizardData.selectedProduct;
    const selectedServiceProduct = selectedProduct?.type === ProductType.SERVICE ? (selectedProduct as IProduct) : undefined;
    const userId = selectedUser?.id ?? navigationState.user?.id ?? null;
    const accountId = store?.wizard.account_id ?? selectedUser?.account_id ?? navigationState.user?.accountId ?? navigationState.iot?.account_id ?? null;
    const submitDisabled =
        isSubmittingRef.current ||
        submissionState === 'submitting' ||
        !accountId ||
        !userId ||
        !selectedServiceProduct ||
        !hasContactInformation ||
        !emailValid;

    const setEmail = (value: string) => {
        setEmailState(value);
        updateWizardData({ serviceProductContact: { email: value, phoneNumber } });
    };

    const setPhoneNumber = (value: string) => {
        setPhoneNumberState(value);
        updateWizardData({ serviceProductContact: { email, phoneNumber: value } });
    };

    const onSubmit = async () => {
        if (submitDisabled || !accountId || !userId || !selectedServiceProduct) return;

        isSubmittingRef.current = true;
        setSubmissionState('submitting');
        setError(null);

        try {
            const token = await DriverAuthenticationManager.getInstance().waitForToken();
            const storedOrder = wizardData.serviceProductOrder as Order | undefined;
            const configuredOrderWorkflowId = store?.functionalitySettings.find((setting) => setting.key === SettingKey.ORDER_WORKFLOW_ID)?.value;
            const order =
                storedOrder ??
                (await OrderClient.postOrder(
                    accountId,
                    {
                        user_id: userId,
                        spot_id: navigationState.spot?.id ?? null,
                        product_id: selectedServiceProduct.id,
                        quantity: 1,
                        state: OrderStatusName.PENDING,
                        workflow_id: typeof configuredOrderWorkflowId === 'string' ? configuredOrderWorkflowId : null
                    },
                    token
                ));

            updateWizardData({
                serviceProductContact: {
                    email: trimmedEmail,
                    phoneNumber: trimmedPhoneNumber
                },
                serviceProductContactSubmitted: true,
                serviceProductUserId: userId,
                serviceProductSpotId: navigationState.spot?.id ?? null,
                serviceProductOrder: order,
                serviceProductOrderId: order.id
            });
            setSubmissionState('submitted');
        } catch (error) {
            Logger.error('Failed to create service product order', error);
            setError(
                intl.formatMessage({
                    id: 'kiosk.wizard.verify-contact-information.submit-error',
                    description: 'Error shown when the kiosk cannot create the service request order',
                    defaultMessage: 'We could not submit your request. Please try again.'
                })
            );
            setSubmissionState('error');
        } finally {
            isSubmittingRef.current = false;
        }
    };

    return {
        email,
        setEmail,
        phoneNumber,
        setPhoneNumber,
        submitDisabled,
        error,
        isSubmitting: submissionState === 'submitting',
        submitted: submissionState === 'submitted',
        continueEnabled,
        selectedServiceProduct,
        onSubmit,
        onContinue: continueEnabled ? onContinue : onHome,
        resetError: () => setError(null)
    };
};

export default useVerifyContactInformation;
